import { Suspense, useCallback, useState } from 'react';
import { ActivityIndicator, ScrollView } from 'react-native';
import { useAtomValue, useStore } from 'jotai';
import { useQueryClient } from '@tanstack/react-query';
import {
  Divider,
  Flex,
  PrimaryButton,
  StackScreen,
  useSafeArea,
  useScreenSize,
  VerticalSpacing,
  Typography,
} from '@banhala/ui-kit';
import { bridge } from 'shared/src/utils';
import { useHandleBackPressed, useOpenDeeplink, useReportTTFD } from 'shared/src/hooks';
import { useMyInfo } from 'shared/src/queries/useMyInfo';
import { SearchIcon, CartIcon } from 'shared/src/component';
import { AnalyticsScreen } from 'shared/src/context';
import { queryKey as sharedQueryKey } from 'shared/src/constant';
import { queryKey as myPageQueryKey } from 'mypage/src/constant/queryKey';
import { useMyPageRoute } from 'mypage/src/hooks/useMyPageRoute';
import { useLogViewMyPersonalization } from './hooks/useLogViewMyPersonalization';
import { useLogCompleteEditMyPersonalization } from './hooks/useLogCompleteEditMyPersonalization';
import { useBeautyInfo } from './queries/useBeautyInfo';
import { usePatchMemberInfo } from './queries/usePatchMemberInfo';
import { useSizePickerBottomSheet } from './components/SizePickerBottomSheet';
import MySizeSection from './components/MySizeSection';
import MyBeautySection from './components/MyBeautySection';
import PrivacyAgreement from './components/PrivacyAgreement';
import {
  useMyPersonalizationForm,
  useMyPersonalizationFormHydration,
} from './state';
import type { GetMemberInfoResponse } from 'shared/src/api/getMemberInfo/types';
import type { GetBeautyInfoResponse } from 'shared/src/api/myPageService/getBeautyInfo/types';
import {
  BODY_SIZE_OPTIONS,
  BOTTOM_SIZE_RANGE,
  formatRangeBoundary,
  HEIGHT_RANGE,
  SHOES_SIZE_RANGE,
  TOP_SIZE_DEFAULT_CODE,
  WEIGHT_RANGE,
} from './constants';

const buildRangeOptions = (
  min: number,
  max: number,
  step: number,
  formatter?: (v: number) => string,
) => {
  const options = [];
  for (let v = min; v <= max; v += step) {
    options.push({ label: formatter ? formatter(v) : `${v}`, value: v });
  }
  return options;
};

const heightOptions = buildRangeOptions(
  HEIGHT_RANGE.min,
  HEIGHT_RANGE.max,
  1,
  v => formatRangeBoundary(v, HEIGHT_RANGE.min, HEIGHT_RANGE.max, 'cm'),
);
const weightOptions = buildRangeOptions(
  WEIGHT_RANGE.min,
  WEIGHT_RANGE.max,
  1,
  v => formatRangeBoundary(v, WEIGHT_RANGE.min, WEIGHT_RANGE.max, 'kg'),
);
const topOptions = BODY_SIZE_OPTIONS.map(o => ({
  label: o.label,
  value: o.code,
}));
const bottomOptions = buildRangeOptions(
  BOTTOM_SIZE_RANGE.min,
  BOTTOM_SIZE_RANGE.max,
  1,
  v => formatRangeBoundary(v, BOTTOM_SIZE_RANGE.min, BOTTOM_SIZE_RANGE.max, ''),
);
const shoesOptions = buildRangeOptions(
  SHOES_SIZE_RANGE.min,
  SHOES_SIZE_RANGE.max,
  SHOES_SIZE_RANGE.step,
  v => formatRangeBoundary(v, SHOES_SIZE_RANGE.min, SHOES_SIZE_RANGE.max, ''),
);

type NumericFieldKey = 'height' | 'weight' | 'top' | 'bottom' | 'shoes';

const MyPersonalizationFormBody = ({
  myInfo,
  beautyInfo,
  from,
  onClose,
}: {
  myInfo: GetMemberInfoResponse;
  beautyInfo: GetBeautyInfoResponse;
  from: string | undefined;
  onClose: () => void;
}) => {
  useMyPersonalizationFormHydration({
    height: myInfo.height ?? null,
    weight: myInfo.weight ?? null,
    top: myInfo.top ?? null,
    bottom: myInfo.bottom ?? null,
    shoes: myInfo.shoes ?? null,
    skinTone: myInfo.skin_tone ?? null,
    skinType: myInfo.skin_type ?? null,
    skinProblems: myInfo.skin_problems ?? null,
    isAlreadyAgreed: myInfo.custom_personal_info_collection_agreed ?? false,
  });

  const atoms = useMyPersonalizationForm();
  const store = useStore();
  const isSubmitEnabled = useAtomValue(atoms.isSubmitEnabledAtom);
  const showPrivacyAgreement = useAtomValue(atoms.showPrivacyAgreementAtom);

  const { mutate: patchMemberInfo, isPending } = usePatchMemberInfo();
  const logComplete = useLogCompleteEditMyPersonalization();
  const [tooltipDismissKey, setTooltipDismissKey] = useState(0);
  const handleScrollBeginDrag = useCallback(() => {
    setTooltipDismissKey(k => k + 1);
  }, []);

  const { show: showSizePicker } = useSizePickerBottomSheet();
  const { openDeeplink } = useOpenDeeplink();
  const { bottom: safeAreaBottom } = useSafeArea();
  const { width: contentWidth } = useScreenSize();

  useLogViewMyPersonalization({
    height: myInfo.height ?? null,
    weight: myInfo.weight ?? null,
    topLabel:
      BODY_SIZE_OPTIONS.find(o => o.code === myInfo.top)?.label ?? null,
    bottom: myInfo.bottom ?? null,
    shoes: myInfo.shoes ?? null,
    skinTone: myInfo.skin_tone ?? null,
    skinType: myInfo.skin_type ?? null,
    skinProblems: myInfo.skin_problems ?? null,
    from,
  });

  const handlePickSize = async (
    title: string,
    options: { label: string; value: number }[],
    fieldKey: NumericFieldKey,
    defaultValue?: number,
  ) => {
    const result = await showSizePicker({
      title,
      options,
      current: store.get(atoms.formAtom)[fieldKey],
      defaultValue,
    });
    if (result.type !== 'confirm') return;
    store.set(atoms.formAtom, prev => ({
      ...prev,
      [fieldKey]: result.data.selected,
    }));
  };

  const handleSave = () => {
    if (isPending || !isSubmitEnabled) return;

    const form = store.get(atoms.formAtom);
    const emptyPersonalizationInfo = store.get(
      atoms.emptyPersonalizationInfoAtom,
    );
    const skinProblemChanged = store.get(atoms.skinProblemChangedAtom);
    const topLabel = store.get(atoms.topLabelAtom);

    patchMemberInfo(
      {
        height: form.height,
        weight: form.weight,
        top: form.top,
        bottom: form.bottom,
        shoes: form.shoes,
        skin_tone: form.skinTone?.sno ?? null,
        skin_type: form.skinType?.sno ?? null,
        skin_problems: form.skinProblems.map(p => p.sno),
        custom_personal_info_collection_agreed: emptyPersonalizationInfo
          ? null
          : form.checksPrivacyAgreement,
        skin_problem_changed: skinProblemChanged,
      },
      {
        onSuccess: () => {
          logComplete({
            height: form.height,
            weight: form.weight,
            topLabel,
            bottom: form.bottom,
            shoes: form.shoes,
            skinTone: form.skinTone,
            skinType: form.skinType,
            skinProblems: form.skinProblems,
            from,
          });
          onClose();
        },
        onError: () => {
          bridge.toast('내 정보 저장에 실패했어요');
        },
      },
    );
  };

  const handleOpenFindPersonalColor = () => {
    openDeeplink(
      'ably://webview?title_bar=false&url=personal-color/?from=MY_PERSONALIZATION',
    );
  };

  const handleOpenPrivacyPolicy = () => {
    openDeeplink('ably://webview?url=legal-notice/privacy-policy');
  };

  return (
    <>
      <ScrollView
        contentContainerStyle={{
          width: contentWidth,
          marginHorizontal: 'auto',
          paddingHorizontal: 16,
        }}
        showsVerticalScrollIndicator={false}
        onScrollBeginDrag={handleScrollBeginDrag}
        style={{ flex: 1 }}
      >
        <Flex padding={{ top: 16, bottom: 8 }}>
          <Typography type="body2" color="content_secondary">
            내 정보를 입력하면 맞춤 상품을 추천받고 나와 비슷한 리뷰만 모아서
            볼 수 있어요
          </Typography>
        </Flex>

        <MySizeSection
          tooltipDismissKey={tooltipDismissKey}
          onPressHeight={() =>
            handlePickSize('키', heightOptions, 'height', HEIGHT_RANGE.default)
          }
          onPressWeight={() =>
            handlePickSize(
              '체중',
              weightOptions,
              'weight',
              WEIGHT_RANGE.default,
            )
          }
          onPressTop={() =>
            handlePickSize(
              '상의 사이즈',
              topOptions,
              'top',
              TOP_SIZE_DEFAULT_CODE,
            )
          }
          onPressBottom={() =>
            handlePickSize(
              '하의 사이즈',
              bottomOptions,
              'bottom',
              BOTTOM_SIZE_RANGE.default,
            )
          }
          onPressShoes={() =>
            handlePickSize(
              '신발 사이즈',
              shoesOptions,
              'shoes',
              SHOES_SIZE_RANGE.default,
            )
          }
        />

        <VerticalSpacing size={20} />

        <MyBeautySection
          tooltipDismissKey={tooltipDismissKey}
          skinToneOptions={beautyInfo.skin_tone_list}
          skinTypeOptions={beautyInfo.skin_type_list}
          skinProblemOptions={beautyInfo.skin_problem_list}
          onPressFindPersonalColor={handleOpenFindPersonalColor}
        />

        <VerticalSpacing size={60} />
      </ScrollView>

      <Flex width={contentWidth} alignSelf="center">
        <Divider size="small" />
        {showPrivacyAgreement && (
          <PrivacyAgreement onViewAll={handleOpenPrivacyPolicy} />
        )}
        <Flex
          padding={{
            horizontal: 16,
            bottom: safeAreaBottom + 16,
            top: showPrivacyAgreement ? 0 : 16,
          }}
        >
          <PrimaryButton
            variant="fill"
            color="pink"
            size="xlarge"
            onPress={handleSave}
            state={!isSubmitEnabled || isPending ? 'disabled' : undefined}
          >
            저장하기
          </PrimaryButton>
        </Flex>
      </Flex>
    </>
  );
};

const MyPersonalizationContent = ({
  onClose,
}: {
  onClose: () => void;
}) => {
  const route = useMyPageRoute<'MyPersonalization'>();
  const { from } = route.params ?? {};

  const { data: myInfo } = useMyInfo();
  const { data: beautyInfo } = useBeautyInfo();

  if (!myInfo) {
    return (
      <Flex.Center flex={1}>
        <ActivityIndicator />
      </Flex.Center>
    );
  }

  return (
    <MyPersonalizationFormBody
      myInfo={myInfo}
      beautyInfo={beautyInfo}
      from={from}
      onClose={onClose}
    />
  );
};

const MyPersonalization = () => {
  const { showBackButton, onPressBack } = useHandleBackPressed();
  const queryClient = useQueryClient();

  useReportTTFD();

  // 진입 시 캐시 자체를 제거. useEffect로는 children Suspense 쿼리가
  // stale 캐시를 먼저 읽어 form hydration이 옛 값으로 락되는 race가 있음.
  // useState lazy initializer는 render 단계에서 children 마운트 직전에 실행되어
  // useSuspenseQuery가 비어있는 캐시를 보고 재fetch하도록 보장.
  useState(() => {
    queryClient.resetQueries({ queryKey: sharedQueryKey.myInfo });
    queryClient.resetQueries({ queryKey: myPageQueryKey.beautyInfo() });
    return null;
  });

  return (
    <AnalyticsScreen screen="MY_PERSONALIZATION">
      <StackScreen
        title="맞춤 정보"
        hideBackButton={!showBackButton}
        onPressBack={onPressBack}
        rightIcons={[<SearchIcon key="search" />, <CartIcon key="cart" />]}
      >
        <Suspense
          fallback={
            <Flex.Center flex={1}>
              <ActivityIndicator />
            </Flex.Center>
          }
        >
          <MyPersonalizationContent onClose={onPressBack} />
        </Suspense>
      </StackScreen>
    </AnalyticsScreen>
  );
};

export default MyPersonalization;
